import React from 'react';
import { FilteredMedia } from '../../utils/mediaFiltering';
import Modal from '../ui/Modal';
import { Loader, ImageIcon, VideoIcon, FileIcon } from 'lucide-react';
import './SOPMediaSelector.css';

interface SOPMediaSelectorProps {
  open: boolean;
  onClose: () => void;
  media: FilteredMedia | null;
  loading: boolean;
  error: string | null;
  onMediaSelect: (mediaUrl: string, mediaId: string) => void;
}

const SOPMediaSelector: React.FC<SOPMediaSelectorProps> = ({
  open,
  onClose,
  media,
  loading,
  error,
  onMediaSelect,
}) => {
  if (loading) {
    return (
      <Modal open={open} onClose={onClose} title="Select Media">
        <div className="flex items-center justify-center p-8">
          <Loader className="animate-spin w-6 h-6 mr-2" />
          <span>Loading media...</span>
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal open={open} onClose={onClose} title="Select Media">
        <div className="text-center p-8">
          <p className="text-red-500">Error: {error}</p>
        </div>
      </Modal>
    );
  }

  if (!media) {
    return (
      <Modal open={open} onClose={onClose} title="Select Media">
        <div className="text-center p-8">
          <p>No media found</p>
        </div>
      </Modal>
    );
  }

  const hasAnyMedia = media.extractedFrames.length > 0 || 
                     media.images.length > 0 ||
                     media.extractedRenders.length > 0;

  return (
    <Modal open={open} onClose={onClose} title="Select Media for Step">
      <div className="max-h-[70vh] overflow-y-auto">
        {/* Extracted Frames */}
        {media.extractedFrames.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <VideoIcon className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">Video Frames</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {media.extractedFrames.map((frame) => (
                <div
                  key={frame.id}
                  className="border border-gray-300 rounded-lg p-2 cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => onMediaSelect(frame.url, frame.image_ref_id || frame.id)}
                >
                  <img
                    src={frame.url}
                    alt={`Frame at ${frame.timestamp}s`}
                    className="w-full h-24 object-cover rounded"
                  />
                  <p className="text-xs text-center mt-1">{frame.timestamp.toFixed(1)}s</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Extracted Renders */}
        {media.extractedRenders.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <FileIcon className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">3D Model Renders</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {media.extractedRenders.map((render) => (
                <div
                  key={render.id}
                  className="border border-gray-300 rounded-lg p-2 cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => onMediaSelect(render.url, render.image_ref_id || render.id)}
                >
                  <img
                    src={render.url}
                    alt={`${render.view_type} ${render.view_name} view`}
                    className="w-full h-24 object-cover rounded"
                  />
                  <p className="text-xs text-center mt-1">
                    {render.view_type} {render.view_name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Original Images */}
        {media.images.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <ImageIcon className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">Images</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {media.images.map((image) => (
                <div
                  key={image.id}
                  className="border border-gray-300 rounded-lg p-2 cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => onMediaSelect(image.url, image.image_ref_id || image.id)}
                >
                  <img
                    src={image.url}
                    alt="SOP Image"
                    className="w-full h-24 object-cover rounded"
                  />
                  <p className="text-xs text-center mt-1">Image</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!hasAnyMedia && (
          <div className="text-center p-8">
            <p className="text-gray-500">No usable media found</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SOPMediaSelector; 